import React, { useState } from 'react';
import { 
  ExternalLink, 
  Database, 
  Zap, 
  Globe,
  ArrowRight,
  Filter
} from 'lucide-react';
import './Portfolio.css';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: "Hospital Management System",
      category: "automation",
      description: "Comprehensive hospital management solution with patient tracking, staff scheduling, and automated reporting using advanced automation tools.",
      image: "/api/placeholder/400/250",
      technologies: ["Database APIs", "Power Automate", "SQL Database", "Power BI"],
      results: ["90% reduction in manual scheduling", "Real-time patient tracking", "Automated compliance reporting"],
      gradient: "linear-gradient(135deg, #6b7280 0%, #374151 100%)"
    },
    {
      id: 2,
      title: "Fleet Management System",
      category: "automation",
      description: "Advanced fleet tracking and maintenance automation system with real-time GPS integration and predictive maintenance scheduling.",
      image: "/api/placeholder/400/250",
      technologies: ["GPS API", "Data Management", "IoT Sensors", "Automated Workflows"],
      results: ["75% improvement in fleet utilization", "Predictive maintenance alerts", "Reduced operational costs by 40%"],
      gradient: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)"
    },
    {
      id: 3,
      title: "Property Management Platform",
      category: "web",
      description: "Full-stack property management web application with tenant portals, automated rent collection, and maintenance tracking.",
      image: "/api/placeholder/400/250",
      technologies: ["React", "Node.js", "MongoDB", "Payment APIs"],
      results: ["Automated rent collection", "95% tenant satisfaction", "Streamlined maintenance requests"],
      gradient: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
    },
    {
      id: 4,
      title: "Custom Application Development",
      category: "web",
      description: "Bespoke business application tailored to specific client requirements with custom workflows and integrations.",
      image: "/api/placeholder/400/250",
      technologies: ["Custom Framework", "API Integration", "Cloud Deployment", "Security Protocols"],
      results: ["100% client specification match", "Scalable architecture", "Enhanced business efficiency"],
      gradient: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
    },
    {
      id: 5,
      title: "CRM Automation System",
      category: "automation",
      description: "Intelligent CRM system with automated lead scoring, follow-up sequences, and integrated communication workflows.",
      image: "/api/placeholder/400/250",
      technologies: ["CRM Platforms", "Zapier", "Email APIs", "Analytics Dashboard"],
      results: ["85% increase in lead conversion", "Automated follow-up sequences", "Real-time sales analytics"],
      gradient: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)"
    },
    {
      id: 6,
      title: "Project Management Solution",
      category: "automation",
      description: "Enterprise project management system with resource allocation, timeline tracking, and automated progress reporting.",
      image: "/api/placeholder/400/250",
      technologies: ["Project Tools", "Gantt Charts", "Resource Planning", "Automated Reports"],
      results: ["60% faster project delivery", "Improved resource utilization", "Real-time project visibility"],
      gradient: "linear-gradient(135deg, #6b7280 0%, #374151 100%)"
    }
  ];

  const filters = [
    { key: 'all', label: 'All Projects', icon: <Filter size={18} /> },
    { key: 'data', label: 'Data Solutions', icon: <Database size={18} /> },
    { key: 'automation', label: 'Automation', icon: <Zap size={18} /> },
    { key: 'web', label: 'Web Development', icon: <Globe size={18} /> }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="portfolio" className="portfolio section section-light">
      <div className="container">
        <div className="portfolio-header">
          <h2>Our Work</h2>
          <p>Explore our successful projects and the transformative results we've delivered for our clients</p>
        </div>

        <div className="portfolio-filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.icon}
              {filter.label}
            </button>
          ))}
        </div>

        <div className="portfolio-grid">
          {filteredProjects.map((project) => (
            <div key={project.id} className="portfolio-card">
              <div className="portfolio-card-header" style={{ background: project.gradient }}>
                <div className="portfolio-image">
                  <div className="image-placeholder">
                    {project.category === 'smartsheets' && <Database size={40} />}
                    {project.category === 'automation' && <Zap size={40} />}
                    {project.category === 'web' && <Globe size={40} />}
                  </div>
                </div>
              </div>
              
              <div className="portfolio-card-body">
                <h3>{project.title}</h3>
                <p className="project-description">{project.description}</p>
                
                <div className="technologies">
                  <h4>Technologies Used:</h4>
                  <div className="tech-tags">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>

                <div className="results">
                  <h4>Key Results:</h4>
                  <ul>
                    {project.results.map((result, index) => (
                      <li key={index}>{result}</li>
                    ))}
                  </ul>
                </div>

                <div className="portfolio-card-footer">
                  <a 
                    href="https://app.smartsheet.com/b/form/819a65d4d35b4bf589f8f0cdd783842c" 
                    className="project-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="portfolio-cta">
          <div className="cta-content">
            <h3>Ready to Start Your Project?</h3>
            <p>Let's discuss how we can help you achieve similar results for your business.</p>
            <a 
              href="https://app.smartsheet.com/b/form/819a65d4d35b4bf589f8f0cdd783842c" 
              className="btn btn-primary btn-large"
              target="_blank"
              rel="noopener noreferrer"
            >
              Start Your Project <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
